import { SortOption } from 'components/Buttons/TableButtons/TableButtonSort'
import { DataTypeColumn } from './commonColumnHelpers'

export type SortDirection = 'asc' | 'desc'

/**
 * Builds sort options for TableButtonSort from sortable fields and column configs.
 *
 * For each field, generates two SortOption entries (desc and asc).
 * Looks up the label from the provided column configs in order,
 * falling back to customLabels, then to the raw field name.
 */
export function commonSortOptionsHelper<T extends string>(
  sortableFields: readonly T[],
  columnConfigs: Partial<Record<T, DataTypeColumn>>[],
  customLabels?: Partial<
    Record<T, string | ((field: T, direction: SortDirection) => string)>
  >
): SortOption<T>[] {
  return sortableFields.flatMap((field) => {
    const labelDesc = resolveLabel(field, 'desc', columnConfigs, customLabels)
    const labelAsc = resolveLabel(field, 'asc', columnConfigs, customLabels)
    return [
      { label: labelDesc, value: field, direction: 'desc' as SortDirection },
      { label: labelAsc, value: field, direction: 'asc' as SortDirection },
    ]
  })
}

function resolveLabel<T extends string>(
  field: T,
  direction: SortDirection,
  columnConfigs: Partial<Record<T, DataTypeColumn>>[],
  customLabels?: Partial<
    Record<T, string | ((field: T, direction: SortDirection) => string)>
  >
): string {
  let result: string = field
  for (const config of columnConfigs) {
    if (config[field]) {
      result = config[field].header
    }
  }
  if (customLabels?.[field]) {
    const customLabel = customLabels[field]
    result =
      typeof customLabel === 'function'
        ? customLabel(field, direction)
        : customLabel
  }
  return result
}
